import { useEffect, useState } from 'react'
import { STAGE_FLOW, STAGE_META, type OpportunityStage } from '@reptree/shared'
import { useForceLight } from '../lib/theme'
import { prefersReducedMotion, useScrollStyle } from '../lib/motion'
import { TreeMark } from '../components/Logo'
import { MarketingChrome, MarketingFooter, Testimonials } from '../components/marketing/shared'
import { Reveal } from '../components/marketing/Reveal'
import { CineBand, Lines, DriftBlock, StatCounter, BlobField, HeroSlideshow, Preloader } from '../components/marketing/blocks'
import { InboxMock, JourneyMock, PortalMock, TapeVaultMock } from '../components/marketing/ProductMock'
import { PricingTable } from '../components/marketing/PricingTable'
import { FAQ } from '../components/marketing/FAQ'

// High-res set & actor photography (Unsplash, all verified, no AI). Crops chosen per frame.
const u = (id: string, params: string) => `https://images.unsplash.com/photo-${id}?auto=format&${params}`
const IMG = {
  // Hero reel: the same warm warehouse shoot, then a blue stage, a location day, a period set.
  // All verified, authentic on-set photography (no AI, no glossy stock) — tonally matched.
  reel: [
    { src: u('1632187989763-c9c620420b4d', 'fit=crop&w=2400&q=80'), alt: 'A film crew gathered around the camera on a working set' },
    { src: u('1632187981988-40f3cbaeef5e', 'fit=crop&w=2400&q=80'), alt: 'A warehouse film set with lighting rigs, monitors and crew' },
    { src: u('1637250096679-c10f2751def8', 'fit=crop&w=2400&q=80'), alt: 'A crew at the monitor cart on a blue-lit studio stage' },
    { src: u('1657184925977-30a2d89fe72f', 'fit=crop&w=2400&q=80'), alt: 'A crew filming on location across a green hillside' },
    { src: u('1518929458119-e5bf444c30f4', 'fit=crop&w=2400&q=80'), alt: 'Crew with a camera dolly on a western set' },
  ],
  // Full-bleed interlude bands (same shoot family as the hero reel).
  warehouse: u('1632187981988-40f3cbaeef5e', 'fit=crop&w=2400&q=80'),
  fieldCrew: u('1657184925977-30a2d89fe72f', 'fit=crop&w=2400&q=80'),
  reels: u('1542204165-65bf26472b9b', 'fit=crop&w=1600&q=80'),
  screen: u('1485095329183-d0797cdc5676', 'fit=crop&w=2000&q=80'),
}

/**
 * Boot gate for the soft loading veil: lifts once the display face AND the hero's first
 * frame are in (min 450ms so it never flashes, max 2.6s so a slow CDN can't hold the page).
 */
function useBootReady(heroReady: boolean) {
  const [minDone, setMinDone] = useState(false)
  const [fontsDone, setFontsDone] = useState(false)
  const [timedOut, setTimedOut] = useState(false)
  useEffect(() => {
    const a = setTimeout(() => setMinDone(true), 450)
    const b = setTimeout(() => setTimedOut(true), 2600)
    if (document.fonts?.ready) document.fonts.ready.then(() => setFontsDone(true)).catch(() => setFontsDone(true))
    else setFontsDone(true)
    return () => { clearTimeout(a); clearTimeout(b) }
  }, [])
  if (prefersReducedMotion()) return timedOut || (fontsDone && heroReady) || minDone
  return minDone && (timedOut || (fontsDone && heroReady))
}

export default function Landing() {
  useForceLight()
  const [heroReady, setHeroReady] = useState(false)
  const ready = useBootReady(heroReady)
  return (
    <div id="top" className="overflow-x-clip bg-bone text-ink">
      <Preloader done={ready} />
      <MarketingChrome />
      <Hero ready={ready} onFirstFrame={() => setHeroReady(true)} />
      <Manifesto />
      {/* Full-bleed cinematic interlude — same photographic register as the hero. */}
      <CineBand
        src={IMG.warehouse}
        alt="A warehouse film set with lighting rigs and crew"
        eyebrow="Built on set"
        lines={['For the people', 'behind the take.']}
      />
      <FeatureRows />
      <Platform />
      <Numbers />
      <CineBand
        src={IMG.fieldCrew}
        alt="A crew filming on location across a green hillside"
        eyebrow="The payoff"
        lines={['Less admin.', 'More callbacks.']}
      />
      <Experience />
      <Pricing />
      <section className="mx-auto max-w-[1320px] px-6 section-gap md:px-10">
        <Reveal><p className="eyebrow mb-10">What it replaces</p></Reveal>
        <Testimonials />
      </section>
      <Faq />
      <FinalCta />
      <MarketingFooter />
    </div>
  )
}

/* ── Hero: a full-screen reel of real sets behind the giant Rep·Tree wordmark ───────── */
function Hero({ ready, onFirstFrame }: { ready: boolean; onFirstFrame: () => void }) {
  // Scroll choreography writes styles directly — no React re-render per scroll frame.
  const drift = useScrollStyle<HTMLDivElement>((el, y) => {
    el.style.transform = `translateY(${y * -0.14}px)`
    el.style.opacity = String(Math.max(0, 1 - y / 640))
  })
  return (
    <header className="relative flex min-h-[100svh] flex-col justify-center overflow-hidden">
      <HeroSlideshow slides={IMG.reel} play={ready} onFirstFrame={onFirstFrame} />

      {/* Eyebrow + giant wordmark in cream over the photography */}
      <div ref={drift} className="relative z-10 px-3 pb-[10vh] text-center text-paper">
        <Reveal as="p" play={ready} className="eyebrow mb-6 !text-paper/80">For film &amp; TV talent agents and managers</Reveal>
        <Lines
          as="h1"
          play={ready}
          lines={[
            <span key="wm" className="inline-flex items-baseline whitespace-nowrap">
              Rep
              <TreeMark className="mx-[0.04em] h-[0.78em] w-[0.78em] self-center" />
              Tree
            </span>,
          ]}
          className="display-giant txt-cine select-none font-display"
          stagger={0}
        />
        <Reveal delay={420} play={ready} as="p" className="mx-auto mt-2 max-w-md text-base text-paper/75 md:text-lg">
          The home base for talent representation.
        </Reveal>
      </div>

      {/* Bottom-left tagline + scroll cue (cream over scrim) */}
      <div className="txt-cine absolute bottom-8 left-6 z-20 max-w-[16rem] text-paper md:left-10 md:max-w-xs">
        <Reveal delay={350} play={ready}>
          <p className="font-display text-xl leading-snug md:text-2xl">
            Self-tapes, auditions &amp; bookings — <em>calmly, in one place.</em>
          </p>
          <p className="mt-4 border-t border-paper/25 pt-3 text-sm text-paper/75">
            <span className="scroll-cue mr-2 inline-block">↓</span>Scroll to explore
          </p>
        </Reveal>
      </div>

      {/* Bottom-center floating CTA card (Floema's corner card, recentered above the counter) */}
      <Reveal delay={520} play={ready} className="absolute bottom-24 right-6 z-20 hidden w-72 md:right-10 md:block">
        <div className="overflow-hidden rounded-[20px] bg-paper/95 p-2.5">
          <div className="h-24 overflow-hidden rounded-[14px]">
            <img src={IMG.reels.replace('w=1600', 'w=640')} alt="Film reels" loading="lazy" decoding="async" className="h-full w-full object-cover" />
          </div>
          <div className="flex items-center justify-between gap-2 p-3">
            <p className="text-sm leading-tight text-ink">First month free.<br /><span className="text-ink/55">$20 / seat after.</span></p>
            <a href="/login?mode=signup" className="btn-brass !px-4 !py-2 text-xs uppercase tracking-[0.12em]">Start</a>
          </div>
        </div>
      </Reveal>
    </header>
  )
}

/* ── Manifesto: the problem, line by line ─────────────────────────────────────────── */
function Manifesto() {
  return (
    <section className="relative mx-auto max-w-[1320px] px-6 section-gap md:px-10">
      <Reveal><p className="eyebrow mb-8">01 — Why RepTree</p></Reveal>
      <Lines
        lines={[
          'Five casting sites. A hundred emails.',
          'Tapes lost to expired links.',
          <>There is <em className="text-forest">a better way to represent.</em></>,
        ]}
        className="text-display max-w-5xl font-display"
      />
      <Reveal delay={250} className="mt-10 grid gap-6 md:grid-cols-[1fr_1fr]">
        <p className="max-w-md text-lg leading-relaxed text-[color:rgb(var(--muted))]">
          RepTree is the home base for the whole job: every audition request lands in one inbox, every
          self-tape lives in one vault, and every client can see exactly where they stand.
        </p>
        <p className="max-w-md text-lg leading-relaxed text-[color:rgb(var(--muted))] md:justify-self-end">
          Built with working agents — around a real audition log, a real roster, and the real Tuesday-night
          scramble before a Wednesday deadline.
        </p>
      </Reveal>
    </section>
  )
}

/* ── Feature rows: numbered editorial blocks with drifting tints + curtain photos ──── */
const FEATURES = [
  {
    n: '02',
    tint: 'bg-tint1',
    mock: <InboxMock />,
    title: ['Every audition,', 'one inbox.'],
    copy: 'Forward casting emails from any source — Casting Networks, Actors Access, Spotlight, plain email — and they land as ready-to-track auditions. Paste a spreadsheet to import your whole history.',
    points: ['Client matched, role and deadline parsed', 'Bulk import in one paste'],
  },
  {
    n: '03',
    tint: 'bg-tint2',
    mock: <TapeVaultMock />,
    title: ['A vault for', 'every take.'],
    copy: 'Collect self-tapes on branded links, review every version, request retakes with notes, reorder takes, and release the final cut to casting. Stored permanently at full quality — the opposite of an expired transfer link.',
    points: ['Versions stack, nothing is lost', 'Approve or request a retake in one tap', 'Move finished cuts to the agency’s Google Drive'],
  },
  {
    n: '04',
    tint: 'bg-tint3',
    mock: <JourneyMock />,
    title: ['The journey,', 'live for clients.'],
    copy: 'Audition → callback → pencil → hold → offer → booked. You move the stage; your client watches it happen in their own portal — without a single “any news?” text.',
    points: ['Nine real lifecycle stages', 'A calm, installable portal for every actor'],
  },
]

/** Feature rows lead with the LIVING product (animated mocks) — we sell the experience, not stock photos. */
function FeatureRows() {
  return (
    <section id="work" className="scroll-mt-24">
      {FEATURES.map((f, i) => (
        <article key={f.n} className="relative mx-auto max-w-[1320px] px-6 section-gap md:px-10">
          {/* The moving block: a tinted panel drifting behind the product side. */}
          <DriftBlock
            speed={i % 2 ? -0.07 : 0.09}
            className={`absolute top-[8%] hidden h-[84%] w-[44%] rounded-[28px] md:block ${f.tint} ${i % 2 ? 'left-[2%]' : 'right-[2%]'}`}
          />
          <div className={`relative grid items-center gap-10 md:grid-cols-2 md:gap-16 ${i % 2 ? '' : 'md:[direction:rtl]'}`}>
            <div className="md:[direction:ltr] md:px-8">
              <Reveal delay={120}>{f.mock}</Reveal>
            </div>
            <div className="md:[direction:ltr]">
              <Reveal>
                <p className="eyebrow">{f.n}</p>
                <Lines lines={f.title} className="text-band mt-4 font-display" />
                <p className="mt-5 max-w-md text-lg leading-relaxed text-[color:rgb(var(--muted))]">{f.copy}</p>
                <ul className="mt-7 max-w-md">
                  {f.points.map((p) => (
                    <li key={p} className="row-rule flex items-center gap-3 py-3.5">
                      <span className="h-1.5 w-1.5 rounded-full bg-forest" aria-hidden />
                      {p}
                    </li>
                  ))}
                </ul>
                <a href="/login?mode=signup" className="group mt-8 inline-flex items-center gap-2 text-forest">
                  <span className="underline decoration-[0.06em] underline-offset-4">Start your free month</span>
                  <span className="transition-transform group-hover:translate-x-1">→</span>
                </a>
              </Reveal>
            </div>
          </div>
        </article>
      ))}
    </section>
  )
}

/* ── The platform: everything beyond the inbox + vault, as a tap-to-read grid ──────── */
const PLATFORM = [
  { label: 'Calendar', blurb: 'Auditions, callbacks and bookings flow onto a shared calendar — and sync out to Google Calendar so nothing collides.' },
  { label: 'Bookkeeping', blurb: 'Track commissions, invoices and payouts per client and per booking, without a second spreadsheet to reconcile.' },
  { label: 'Analytics', blurb: 'Booking rates, audition volume and roster earnings roll up across the whole agency at a glance.' },
  { label: 'Intake forms', blurb: 'Send a branded link; new clients fill in their own profile, sizes and documents — bound to their account.' },
  { label: 'Packages', blurb: 'Assemble submission packages — headshots, reels and résumés — and send them to casting in a few taps.' },
  { label: 'Contacts', blurb: 'Keep casting directors and industry contacts beside the auditions and clients they belong to.' },
  { label: 'White-label', blurb: 'Your logo, your colours. Every client portal and intake link wears the agency’s brand, not ours.' },
  { label: 'Docs & credentials', blurb: 'Contracts, releases and union credentials filed per client — kept on hand, never expired.' },
]

function PlatformGrid() {
  const [active, setActive] = useState(0)
  return (
    <div>
      <p className="eyebrow mb-4">Tap a tool to see what it does</p>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        {PLATFORM.map((p, i) => {
          const on = i === active
          return (
            <button
              key={p.label}
              onClick={() => setActive(i)}
              className={`rounded-card border px-4 py-4 text-left transition ${on ? 'border-transparent bg-tint3' : 'hairline bg-paper hover:bg-tint1'}`}
            >
              <span className="eyebrow">{String(i + 1).padStart(2, '0')}</span>
              <span className={`mt-1 block font-display text-lg ${on ? 'text-forest' : ''}`}>{p.label}</span>
            </button>
          )
        })}
      </div>
      <div className="hairline mt-4 rounded-card bg-paper p-5">
        <p className="text-lg leading-relaxed text-[color:rgb(var(--muted))]">{PLATFORM[active]!.blurb}</p>
      </div>
    </div>
  )
}

function Platform() {
  return (
    <section id="platform" className="mx-auto max-w-[1320px] scroll-mt-24 px-6 section-gap md:px-10">
      <Reveal>
        <p className="eyebrow">05 — The platform</p>
        <Lines lines={['One home base.', 'The whole job.']} className="text-display mt-4 max-w-4xl font-display" />
        <p className="mt-5 max-w-md text-lg leading-relaxed text-[color:rgb(var(--muted))]">
          Beyond the inbox and the vault, RepTree carries the rest of the work too — the scheduling, the
          money, the paperwork, and the numbers behind it all.
        </p>
      </Reveal>
      <Reveal delay={140} className="mt-12"><PlatformGrid /></Reveal>
    </section>
  )
}

/* ── Numbers on a leaf block ───────────────────────────────────────────────────────── */
function Numbers() {
  return (
    <section className="mx-auto max-w-[1320px] px-6 md:px-10">
      <div className="relative overflow-hidden rounded-[28px] bg-tint1 px-8 py-14 md:px-14">
        <BlobField tone="cool" />
        <div className="relative grid gap-10 sm:grid-cols-2 md:grid-cols-4">
          <StatCounter value={1} label="Inbox for every source" />
          <StatCounter value={9} label="Lifecycle stages tracked" />
          <StatCounter value={20} prefix="$" label="Per seat, per month" />
          <StatCounter value={2} label="Free seats to start" />
        </div>
      </div>
    </section>
  )
}

/* ── The experience: the talent portal + the live journey, hands-on ────────────────── */
function Experience() {
  return (
    <section id="journey" className="mx-auto max-w-[1320px] scroll-mt-24 px-6 section-gap md:px-10">
      <Reveal>
        <p className="eyebrow">06 — The experience</p>
        <Lines lines={['Your client sees it', 'the moment it happens.']} className="text-display mt-4 max-w-4xl font-display" />
      </Reveal>
      <div className="mt-12 grid items-center gap-10 md:grid-cols-[1fr_1.4fr] md:gap-16">
        <Reveal delay={80}><PortalMock /></Reveal>
        <Reveal delay={180}><JourneyStepper /></Reveal>
      </div>
    </section>
  )
}

function JourneyStepper() {
  const [active, setActive] = useState<OpportunityStage>('callback')
  const idx = STAGE_FLOW.indexOf(active)
  return (
    <div>
      <p className="eyebrow mb-4">Tap a stage — your client sees it move in real time</p>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
        {STAGE_FLOW.map((s, i) => {
          const on = idx >= i
          return (
            <button
              key={s}
              onClick={() => setActive(s)}
              className={`rounded-card border px-4 py-4 text-left transition ${on ? 'border-transparent bg-tint3' : 'hairline bg-paper hover:bg-tint1'}`}
            >
              <span className="eyebrow">{String(i + 1).padStart(2, '0')}</span>
              <span className={`mt-1 block font-display text-lg ${on ? 'text-forest' : ''}`}>{STAGE_META[s].label}</span>
            </button>
          )
        })}
      </div>
    </div>
  )
}

/* ── Pricing / FAQ / CTA ───────────────────────────────────────────────────────────── */
function Pricing() {
  return (
    <section id="pricing" className="mx-auto max-w-[1320px] scroll-mt-24 px-6 section-gap md:px-10">
      <Reveal>
        <p className="eyebrow">07 — Pricing</p>
        <Lines lines={['One plan.', 'First month free.']} className="text-display mt-4 font-display" />
        <p className="mt-5 max-w-md text-lg text-[color:rgb(var(--muted))]">
          No tiers, no per-feature upsells. The owner plus one teammate are always free.
        </p>
      </Reveal>
      <Reveal delay={140} className="mt-12"><PricingTable /></Reveal>
    </section>
  )
}

function Faq() {
  return (
    <section id="faq" className="mx-auto max-w-[1320px] scroll-mt-24 px-6 section-gap md:px-10">
      <div className="grid gap-10 md:grid-cols-[1fr_1.6fr]">
        <Reveal>
          <p className="eyebrow">08 — Questions</p>
          <Lines lines={['Everything you', 'might wonder.']} className="text-band mt-4 font-display" />
        </Reveal>
        <Reveal delay={120}><FAQ /></Reveal>
      </div>
    </section>
  )
}

function FinalCta() {
  return (
    <section className="mx-auto max-w-[1320px] px-6 pb-20 md:px-10">
      <div className="relative overflow-hidden rounded-[28px] bg-forest px-8 py-20 text-center text-paper md:py-28">
        <div className="pointer-events-none absolute inset-0 opacity-20">
          <img src={IMG.screen} alt="" className="h-full w-full object-cover" />
        </div>
        <div className="relative">
          <Lines
            lines={['Give your clients', 'a home base.']}
            className="text-display font-display"
          />
          <p className="mx-auto mt-5 max-w-md text-paper/75">
            Bring your roster, forward your first casting email, and watch the busywork disappear.
          </p>
          <a href="/login?mode=signup" className="btn-brass btn-lg mt-9 inline-flex !bg-paper !text-forest">
            Start your free month<span className="cta-arrow">→</span>
          </a>
        </div>
      </div>
    </section>
  )
}
